import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import FAQ from "../../components/FAQs";
import coverImage from "./../../images/blog/The Managerless Sprint 2029 How BossDrop AI Automates Middle Manager Tasks And What Stays Human.png"; // swap later if you add a custom graphic
import coverWeb from "./../../images/blog/managerless-sprint-1280.webp";
import { DEMO_LINK } from "../../components/demoBooking";
import CopyLinkButton from "../../components/CopyLinkButton";

export default function Blog_ManagerlessSprint() {
  const title =
    "The Managerless Sprint (2029): How BossDrop AI Automates Middle-Manager Tasks - And What Stays Human";
  const description =
    "A 5-year look at AI project management: BossDrop AI automates task breakdowns, scheduling, risk tracking, and roll-ups - while humans lead strategy, coaching, and craft.";
  const cover = coverImage;
  const canonicalUrl =
    "https://bossdrop.ai/blog/managerless-sprint-bossdrop-ai-2029";
  const published = "2025-09-02";
  const readTime = "9 min read";

  // SEO keywords (most applicable to this topic)
  const keywords = [
    "BossDrop AI",
    "BossDrop AI for project management",
    "AI project management",
    "AI workflow automation",
    "autonomous work management",
    "AI work management",
    "work management",
    "workflow management",
    "project management tool",
    "project tracker",
    "project tracking",
    "task breakdown",
    "resource breakdown structure",
    "resource management software",
    "best task management software",
    "team collaboration tools",
    "productivity software",
    "project management apps",
    "software project management software",
  ];

  const shareUrl = encodeURIComponent(canonicalUrl);
  const shareText = encodeURIComponent(title);
  const onShare = (net) => {
    const map = {
      x: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    document.title = title;
    setCanonical(canonicalUrl);
    setMeta(description);
    setKeywords(keywords.join(", "));
    setOG("og:type", "article");
    setOG("og:title", title);
    setOG("og:description", description);
    setOG("og:url", canonicalUrl);
    setOG("og:image", toAbs(cover));
    setOG("twitter:card", "summary_large_image");
    setOG("twitter:title", title);
    setOG("twitter:description", description);
    setOG("twitter:image", toAbs(cover));

    injectLDJSON("ldjson-blogpost", {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: title,
      description,
      datePublished: published,
      dateModified: published,
      author: { "@type": "Organization", name: "BossDrop" },
      publisher: {
        "@type": "Organization",
        name: "BossDrop",
        logo: { "@type": "ImageObject", url: "https://bossdrop.ai/icon.png" },
      },
      image: toAbs(cover),
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      keywords: keywords.join(", "),
      articleSection: "AI Project Management",
    });

    // reveal on scroll
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="page container">
      {/* Breadcrumbs */}
      <nav
        className="breadcrumbs reveal"
        style={{ "--delay": "0ms" }}
        aria-label="Breadcrumb"
      >
        <Link to="/">Home</Link>
        <span>›</span>
        <Link to="/blog">Blog</Link>
        <span>›</span>
        <span aria-current="page">The Managerless Sprint (2029)</span>
      </nav>

      {/* BossDrop-blue feature hero (your existing hero-feature styles) */}
      <div className="hero-feature reveal" style={{ "--delay": "40ms" }}>
        <div className="hero-gradient" />
        <div className="hero-grid">
          <div className="hero-card">
            <img
              src={coverWeb}
              alt="BossDrop AI automating task breakdowns, scheduling, and risk tracking"
            />
          </div>

          <div className="hero-text">
            <div className="pill">FUTURE OF WORK</div>
            <h1 className="h1">{title}</h1>

            <div className="byline">
              <div className="author">
                <div className="avatar" aria-hidden="true">
                  BD
                </div>
                <div>
                  <div className="author-name">BossDrop Team</div>
                  <div className="meta">
                    {new Date(published).toLocaleDateString()} • {readTime}
                  </div>
                </div>
              </div>

              <div className="share">
                <button aria-label="Share on X" onClick={() => onShare("x")}>
                  {iconX()}
                </button>
                <button
                  aria-label="Share on LinkedIn"
                  onClick={() => onShare("linkedin")}
                >
                  {iconLinkedIn()}
                </button>
                <button
                  aria-label="Share on Facebook"
                  onClick={() => onShare("facebook")}
                >
                  {iconFacebook()}
                </button>
                <CopyLinkButton url={canonicalUrl} />
              </div>
            </div>
          </div>
        </div>

        <div className="hero-cta">
          <span>
            From meetings to momentum:{" "}
            <strong>autonomous work management</strong> with BossDrop AI.
          </span>
          <a
            className="btn btnDemo"
            {...DEMO_LINK}
            aria-label="Book a demo"
          >
            Book a demo
          </a>
        </div>
      </div>

      {/* ---- Article ---- */}
      <article className="article">
        <p className="lede reveal" style={{ "--delay": "80ms" }}>
          Middle managers spend huge chunks of time coordinating: converting
          strategies into <strong>task breakdowns</strong>, keeping the{" "}
          <strong>project tracker</strong> honest, scheduling updates, and
          chasing blockers. Over the next five years,
          <strong> AI project management</strong> systems like{" "}
          <strong>BossDrop AI</strong> will automate most of that coordination - 
          while humans double down on coaching, quality, and strategy. Fewer
          rituals ❌, more results ✅.
        </p>

        <nav
          className="toc reveal"
          style={{ "--delay": "100ms" }}
          aria-label="Table of contents"
        >
          <a href="#timeline">2025 → 2030: automation timeline</a>
          <a href="#automates">What AI automates</a>
          <a href="#stays-human">What stays human</a>
          <a href="#rbs">RBS → Assignments (how it flows)</a>
          <a href="#stack">Your stack: tools that stick</a>
          <a href="#faq">FAQ</a>
        </nav>

        <h2 id="timeline" className="reveal" style={{ "--delay": "120ms" }}>
          2025 → 2030: the automation timeline
        </h2>
        <ul className="reveal bullets" style={{ "--delay": "140ms" }}>
          <li>
            📋 <strong>Now:</strong> <em>AI workflow automation</em> collects
            updates, drafts roll-ups, and nudges owners.
          </li>
          <li>
            🧭 <strong>12–24 months:</strong> Reliable <em>task breakdown</em>{" "}
            from goals → tickets with owner/capacity fits.
          </li>
          <li>
            🧩 <strong>24–36 months:</strong> Dynamic{" "}
            <em>resource breakdown structure</em> (RBS) +{" "}
            <em>resource management software</em> logic balances load across
            teams.
          </li>
          <li>
            🚨 <strong>36–48 months:</strong> Risk-aware scheduling,
            auto-escalations, and portfolio-level <em>project tracking</em>.
          </li>
          <li>
            🧠 <strong>48–60 months:</strong> AI suggests trade-offs across
            cost/time/quality; managers approve and coach.
          </li>
        </ul>

        {/* Figure 1 */}
{/*         <figure className="figure reveal" style={{ "--delay": "160ms" }}>
          <img
            loading="lazy"
            src={cover}
            alt="Automation timeline for AI work management in BossDrop"
            width="1200"
            height="675"
          />
          <figcaption>
            Coordination → automation. Leadership and craft stay human.
          </figcaption>
        </figure> */}

        <h2 id="automates" className="reveal" style={{ "--delay": "180ms" }}>
          What AI automates (and is already good at)
        </h2>
        <ul className="reveal checks" style={{ "--delay": "200ms" }}>
          <li>
            ✅{" "}
            <strong>
              From brief to <em>program to manage projects</em>:
            </strong>{" "}
            goal → <em>task management software</em> items with owners & due
            dates.
          </li>
          <li>
            ✅ <strong>Calendar ops:</strong> Only schedule when risk/decision
            warrants it; otherwise async updates.
          </li>
          <li>
            ✅ <strong>RBS & workload:</strong> Suggested{" "}
            <em>resource breakdown structure</em>, capacity, and{" "}
            <em>project tracker</em> hygiene.
          </li>
          <li>
            ✅ <strong>Portfolio roll-ups:</strong> Clean, weekly narratives
            with KPIs - no slide builds.
          </li>
          <li>
            ✅ <strong>Dependency & blocker sweeps:</strong> Early warnings +
            auto-unblock meetings.
          </li>
        </ul>

        {/* Figure 2 */}
{/*         <figure className="figure reveal" style={{ "--delay": "220ms" }}>
          <img
            loading="lazy"
            src={cover}
            alt="Capacity and dependency aware assignment suggestions"
            width="1200"
            height="675"
          />
          <figcaption>
            Capacity-aware assignment keeps your{" "}
            <strong>project management app</strong> honest.
          </figcaption>
        </figure> */}

        <h2 id="stays-human" className="reveal" style={{ "--delay": "240ms" }}>
          What stays human (and should)
        </h2>
        <ul className="reveal bullets" style={{ "--delay": "260ms" }}>
          <li>🎯 Strategy & prioritization (which bets to place)</li>
          <li>🧑‍🤝‍🧑 Coaching, feedback, and craft standards</li>
          <li>🧪 Ambiguity cracking: defining “good” where context is messy</li>
          <li>🤝 Stakeholder alignment & narrative</li>
        </ul>

        <h2 id="rbs" className="reveal" style={{ "--delay": "280ms" }}>
          Flow: RBS → task breakdown → assignments → tracking
        </h2>
        <ol className="reveal numbered" style={{ "--delay": "300ms" }}>
          <li>
            <strong>Goal in:</strong> Define milestone, constraints, and success
            metrics.
          </li>
          <li>
            <strong>RBS draft:</strong> AI proposes a{" "}
            <em>resource breakdown structure</em> (teams/skills/hours).
          </li>
          <li>
            <strong>Task breakdown:</strong> Tickets with{" "}
            <em>program to manage projects</em> metadata (owner, effort, risk).
          </li>
          <li>
            <strong>Assignment fit:</strong> Skills + workload + timezone →
            owner suggestions.
          </li>
          <li>
            <strong>Project tracking:</strong> SLA checks, risk flags, and
            roll-ups in Slack.
          </li>
        </ol>

        {/* Figure 3 */}
{/*         <figure className="figure reveal" style={{ "--delay": "320ms" }}>
          <img
            loading="lazy"
            src={cover}
            alt="Task breakdown flowing into scheduling and roll-ups"
            width="1200"
            height="675"
          />
          <figcaption>
            <strong>Workflow management</strong> that respects
            humans-in-the-loop.
          </figcaption>
        </figure> */}

        <h2 id="stack" className="reveal" style={{ "--delay": "340ms" }}>
          Your stack (what sticks, what changes)
        </h2>
        <p className="reveal" style={{ "--delay": "360ms" }}>
          Keep your <strong>project management tools</strong> (Asana/Jira),{" "}
          <strong>team collaboration tools</strong> (Slack), and calendars.
          BossDrop overlays <em>autonomous work management</em> - the AI plans,
          assigns, nudges, escalates, and writes the weekly brief. Choose how
          much autonomy you want; approvals are one click.
        </p>

        {/* Quick comparison */}
        <div className="callout info reveal" style={{ "--delay": "380ms" }}>
          <strong>BossDrop AI vs. “best project management software”:</strong>{" "}
          those apps organize work. BossDrop <em>moves</em> work - turning plans
          into progress with <strong>AI workflow automation</strong>.
        </div>

        {/* Shared FAQ component for rich results & consistency */}
        <h2 id="faq" className="reveal" style={{ "--delay": "400ms" }}>
          FAQ
        </h2>
        <FAQ
          reveal={false}
          items={[
            {
              q: "Does this replace managers?",
              a: "No. It replaces coordination chores - status, scheduling, follow-ups - so managers focus on priorities, coaching, and quality.",
            },
            {
              q: "Will it work with our current project management tool?",
              a: "Yes. BossDrop works with Asana or Jira plus Slack, acting as an autonomous layer on your existing stack.",
            },
            {
              q: "Can we control autonomy levels?",
              a: "Absolutely. You can keep the AI in suggest mode or enable auto-assign, auto-nudge, and risk-based scheduling with approvals.",
            },
          ]}
        />

        <div className="cta-row reveal" style={{ "--delay": "420ms" }}>
          <a className="btn" {...DEMO_LINK}>
            Book a demo
          </a>
          <a className="btn btnGhost" href="/#how-it-works">
            See how it works
          </a>
        </div>
      </article>
    </section>
  );
}

/* ---------- helpers ---------- */
function setMeta(content) {
  setOrCreate(
    'meta[name="description"]',
    (el) => el.setAttribute("name", "description"),
    content
  );
}
function setKeywords(content) {
  setOrCreate(
    'meta[name="keywords"]',
    (el) => el.setAttribute("name", "keywords"),
    content
  );
}
function setOG(property, content) {
  if (!content) return;
  const sel = property.startsWith("og:")
    ? `meta[property="${property}"]`
    : `meta[name="${property}"]`;
  setOrCreate(
    sel,
    (el) => {
      property.startsWith("og:")
        ? el.setAttribute("property", property)
        : el.setAttribute("name", property);
    },
    content
  );
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}
function setOrCreate(selector, init, content) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    init(el);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function injectLDJSON(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}
function toAbs(url) {
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

/* --- tiny inline icons --- */
function iconX() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18.9 2H22l-7.7 8.8L22 22h-6.3l-4.9-6-5.6 6H2l8.5-9.4L2 2h6.4l4.4 5.4L18.9 2z"
      />
    </svg>
  );
}
function iconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM0 8h5v16H0V8zm7.5 0h4.8v2.2h.1c.7-1.2 2.3-2.5 4.7-2.5 5 0 5.9 3.3 5.9 7.6V24h-5V16.4c0-1.8 0-4.1-2.5-4.1s-2.9 2-2.9 4v7.7h-5V8z"
      />
    </svg>
  );
}
function iconFacebook() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.7-3.9 1.1 0 2.2.2 2.2.2v2.4h-1.2c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12"
      />
    </svg>
  );
}
